import { useState } from "react";

import type {
  GQLResendWelcomeMailSuccess,
  GQLResendWelcomeMailFailure,
} from "@rx-cs-system/schema";
import { USER_ROLE } from "../../../utils/constants";
import {
  ResendWelcomeMailPage as Component,
  ResendWelcomeMailPageProps,
} from "../../../components/pages/medicalInstitution/ResendWelcomeMailPage";
import { Loading } from "../../../components/atoms/Loading";
import { useMutation } from "@apollo/client/react";
import { GQLMutation } from "@rx-cs-system/schema";
import { RESEND_WELCOME_MAIL } from "../../../graphql/mutations";
import { Navigate, useNavigate, useParams } from "react-router";
import { useValidation } from "../../../hooks/useValidation";
import { AuthContext } from "../../../hooks/useAuthentication";
import { use } from "react";
import { useResendWelcomeMailContract } from "./useResendWelcomeMailContract";

const isFailure = (res: any): res is GQLResendWelcomeMailFailure =>
  res.__typename === "ResendWelcomeMailFailure";

export const ResendWelcomeMailPage = () => {
  const { id: medicalInstitutionId, contractId } = useParams<{
    id: string;
    contractId: string;
  }>();
  if (!medicalInstitutionId || !contractId)
    throw new Error("medicalInstitutionId or contractId is undefined");

  const [snackBar, setSnackBar] =
    useState<ResendWelcomeMailPageProps["snackBar"]>(null);

  const { auth } = use(AuthContext);

  const showSnackBar = (type: "ok" | "error", message: string) => {
    setSnackBar({
      type,
      message,
      isOpen: true,
      close() {
        setSnackBar(null);
      },
    });
  };

  const [resendWelcomeEmail, { loading: resendWelcomeMailProcessing }] =
    useMutation<{
      resendWelcomeMail?: GQLMutation["resendWelcomeMail"];
    }>(RESEND_WELCOME_MAIL, {
      onCompleted: ({ resendWelcomeMail }) => {
        if (isFailure(resendWelcomeMail)) {
          return showSnackBar(
            "error",
            `ウェルカムメールを再送しました。理由: \n${resendWelcomeMail.message}`,
          );
        }
        const result = resendWelcomeMail as GQLResendWelcomeMailSuccess;
        showSnackBar(
          "ok",
          `${result.resendEmailAddress}にウェルカムメールを再送しました。`,
        );
      },
      onError: () => {
        showSnackBar("error", "メール送信に失敗しました。");
      },
    });

  const [otherMailAddress, setOtherMailAddress] = useState("");
  const [resetPassword, setResetPassword] = useState(false);

  const {
    errors: mailInputErrorState,
    isFormValid,
    setTouched,
  } = useValidation(
    {
      otherMailAddress: {
        value: otherMailAddress,
        rules: { email: true },
      },
    },
    [otherMailAddress],
  );

  const { loading, medicalInstitution, contract } =
    useResendWelcomeMailContract(medicalInstitutionId, contractId);

  const navigate = useNavigate();

  if (loading) {
    return <Loading />;
  }

  // 契約が存在しない・製品管理責任者が未設定の場合は再送できないため医療機関詳細に戻す
  if (!medicalInstitution || !contract || !contract.manager) {
    return (
      <Navigate to={`/medicalInstitution/${medicalInstitutionId}`} replace />
    );
  }

  const managerMailAddress = contract.manager.emailAddress ?? "";

  const onClickResendWelcomeMail = (mailAddress?: string) => {
    resendWelcomeEmail({
      variables: {
        input: {
          contractId,
          recipientEmailAddress: mailAddress || managerMailAddress,
          reissueSecretCode: resetPassword,
        },
      },
    });
  };

  const onCancelButtonClick = () => {
    navigate(`/medicalInstitution/${medicalInstitutionId}`);
  };

  return (
    <Component
      resendWelcomeMailTarget={{
        id: medicalInstitutionId,
        contractId,
        name: medicalInstitution.name,
        managerMailAddress,
        managerName: contract.manager.name ?? "",
        address: medicalInstitution.address,
        pdt: contract.pdt,
      }}
      onResendWelcomeMailButtonClick={onClickResendWelcomeMail}
      validationErrors={mailInputErrorState}
      setOtherMailAddress={(mail: string) => {
        setTouched("otherMailAddress");
        setOtherMailAddress(mail);
      }}
      setResetPassword={setResetPassword}
      {...{
        resetPassword,
        otherMailAddress,
        isFormValid,
        resendWelcomeMailProcessing,
        snackBar,
        onCancelButtonClick,
      }}
      isAdminVisible={auth.userRole === USER_ROLE.CS_USER}
    />
  );
};
